import React from 'react';
import { Link } from 'react-router-dom';
import { deriveBillDisplayFields } from '../utils/billDisplay';
import './reportBillListItem.css';

// Presentational report row for one bill. Used by the public report page, the Reports-tab
// preview, and (indirectly, via the same field-derivation logic) the PDF/Word export.
// Deliberately has no tracking/bookmark chrome — only links to BillProfile when explicitly
// told to via `linkToProfile` (gated by viewer auth/subscription state upstream).
export default function ReportBillListItem({ bill, note, showDigest, showAiSummary, linkToProfile, calendarInfo }) {
  const { billDisplay, authorName, location, status, fileLocationDisplay, hearingInfo, isEnrolled } = deriveBillDisplayFields(bill);

  return (
    <>
      {calendarInfo?.showDateHeader && (
        <div className="calendar-date-header">{calendarInfo.headerDateStr}</div>
      )}
      {calendarInfo?.showLocHeader && (
        <div className={`calendar-location-subheader${calendarInfo.headerLocStr.startsWith('A') ? ' calendar-location-subheader--assembly' : calendarInfo.headerLocStr.startsWith('S') ? ' calendar-location-subheader--senate' : ''}${calendarInfo.isFirstLocUnderDate ? '' : ' calendar-location-subheader--subsequent'}`}>
          {calendarInfo.headerLocStr}
        </div>
      )}
      <div className="card mb-2 report-bill-card">
        <div className="card-body py-2 px-3">
          <div className="mb-1">
            {linkToProfile ? (
              <Link to={`/legislation/bill/${bill.bill_id}`} className="bill-number text-decoration-none">
                {billDisplay}
              </Link>
            ) : (
              <span className="bill-number">{billDisplay}</span>
            )}
            <span className="mb-1 text-muted"> - {authorName}</span>
          </div>
          <div className="mb-2 bill-title">{bill.title}</div>
          {bill.LatestVersion && (
            <div className="small text-muted mb-1">
              <span className="fw-bold">Version:</span> {bill.LatestVersion.version_status}
              {bill.LatestVersion.updated_at && ` (${new Date(bill.LatestVersion.updated_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })})`}
            </div>
          )}
          <div className="small text-muted d-flex flex-column flex-sm-row justify-content-between">
            <div>
              <span className="fw-bold">{status}</span>{status === 'Chaptered' && bill.chapter_number && bill.chapter_year && ` - Chapter ${bill.chapter_number}, Statutes of ${bill.chapter_year}`}{status !== 'Chaptered' && location && `: ${location}`}{status !== 'Chaptered' && !isEnrolled && bill.DailyFile?.floor_manager && <em> -- Floor Manager: {bill.DailyFile.floor_manager}</em>}
            </div>
            {fileLocationDisplay && (
              <span className="bill-file-number me-2">{fileLocationDisplay}</span>
            )}
            {hearingInfo && (
              <div className="legislation-hearing-info mt-1 mt-sm-0">
                <u>Next Hearing:</u> {hearingInfo}
              </div>
            )}
          </div>
          {note && (
            <div className="report-bill-note mt-2">
              <i className="bi bi-chat-square-text-fill me-1" />
              <span className="report-bill-note-label">Note:</span> {note}
            </div>
          )}
          {showDigest && bill.summary && (
            <div className="bill-summary-content mt-2 mb-1">
              <u>Digest:</u> {bill.summary}
            </div>
          )}
          {showAiSummary && bill.ai_summary && (
            <div className="bill-summary-content mt-2 mb-1">
              <u>AI Summary:</u> {bill.ai_summary.replace(/^#+\s+[^\n]*\n*/g, '').trim()}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
